import { ActionTree, ActionContext } from 'vuex'
import { IRootState, FeatureFlagsState } from '@/@types'
import { errorHandler } from '@/utils/errors'

import * as defaultState from './state'

type DarklyModule = typeof import('@/utils/darkly')

let darklyModulePromise: Promise<DarklyModule | null> | null = null

const loadDarklyModule = (
  context: ActionContext<FeatureFlagsState, IRootState>
): Promise<DarklyModule | null> => {
  if (!darklyModulePromise) {
    darklyModulePromise = import('@/utils/darkly').catch(async error => {
      darklyModulePromise = null
      const message = errorHandler(error)
      await context.dispatch('app/setError', message, { root: true })
      return null
    })
  }
  return darklyModulePromise
}

const updateFlags = async (
  context: ActionContext<FeatureFlagsState, IRootState>,
  darkly: DarklyModule
) => {
  const newFlags = await darkly.getAllDarklyFlags()
  context.commit('FEATURE_FLAGS_UPDATED', { ...newFlags })
}

const cleanFlagsStore = (context: ActionContext<FeatureFlagsState, IRootState>) => {
  context.commit('FEATURE_FLAGS_UPDATED', defaultState)
}

const actions: ActionTree<FeatureFlagsState, IRootState> = {
  updateFeatureFlags: async (context: ActionContext<FeatureFlagsState, IRootState>) => {
    try {
      const darkly = await loadDarklyModule(context)
      if (!darkly) return
      await updateFlags(context, darkly)
    } catch (error) {
      const message = errorHandler(error)
      context.dispatch('app/setError', message, { root: true })
    }
  },
  initializeDarkly: async (context: ActionContext<FeatureFlagsState, IRootState>) => {
    try {
      const darkly = await loadDarklyModule(context)
      if (!darkly) return
      await darkly.createDarklyClient()
      await updateFlags(context, darkly)
      // add callback for each Launch Darkly feature flag
      Object.keys(context.state).map((flag: string) => {
        darkly.onDarklyFeatureFlagChange(flag, () => {
          updateFlags(context, darkly)
        })
      })
    } catch (error) {
      cleanFlagsStore(context)
    }
  },
  closeDarkly: async (context: ActionContext<FeatureFlagsState, IRootState>) => {
    try {
      const darkly = await loadDarklyModule(context)
      if (!darkly) return
      await darkly.closeDarklyClient()
    } finally {
      cleanFlagsStore(context)
    }
  },
  switchDarklyContextToAnonymous: async (context: ActionContext<FeatureFlagsState, IRootState>) => {
    try {
      const darkly = await loadDarklyModule(context)
      if (!darkly) return
      await darkly.switchDarklyContextToAnonymous()
    } catch (error) {
      cleanFlagsStore(context)
    }
  },
}

export default actions
